import { Country, Fiat, PayoutProvider, Trade, Wallet } from "@models";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TransactionListCoreComponent } from "../transaction-list-core.component";
import { AfterViewInit, Directive } from "@angular/core";
import { TransactionWrapperModalComponent } from "../../../modals/transaction-wrapper-modal/transaction-wrapper-modal.component";
import { BestFeeCollection } from "@models/best_fee_collection";
import { empty, equal, round } from "@helpers/utils";
import { convertTradeToTransaction, toCurrencyByFiatId } from "@helpers/tradable-convert";
import { FiatTransaction, GenericTransaction, IndexTransaction, TradableTransaction } from "@models/transaction";
import { FiatService } from "@main-services/currency";
import { ContactService } from "@main-services/contact.service";
import { PayAccountService } from "@main-services/pay_account.service";
import { Modal, ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";

@Directive()
export abstract class TransactionListDetailComponent extends TransactionListCoreComponent implements AfterViewInit {
	public _txFiat: Fiat = null;
	protected bfcTradeFiat: Fiat = null;
	private debitCardTxCountry: Country = null;

	public constructor(
		protected modalService: ModalService,
		protected tradableWalletListService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected fiatWalletService: FiatWalletService,
		protected fiatService: FiatService,
		protected contactService: ContactService,
		protected payaccountService: PayAccountService,
		protected masterdataService: MasterdataService
	) {
		super(
			modalService,
			tradableWalletListService,
			tradableListService,
			fiatWalletService,
			fiatService,
			contactService,
			payaccountService
		);
	}

	public ngAfterViewInit(): void {
		this.txFiat = this.getTxFiat();
		if (this.isDebitCardTx()) {
			this.debitCardTxCountry = this.masterdataService.getCountryByIso(
				this.transaction.getMerchantDetails().getCountryIso()
			);
		}
	}

	public getBfcTradeFiat(): Fiat {
		// We only want to call this for a BFC Transaction and ONLY once
		if (!this.bfcTradeFiat && this.transaction.getIsBfc() && this.transaction instanceof TradableTransaction) {
			this.setBfcTradeFiat();
		}
		return this.bfcTradeFiat;
	}

	public setBfcTradeFiat(): void {
		// Is called once in order to set bfcTradeFiat, then never called again
		this.bfcTradeFiat = this.fiatService.getItemById(this.getBfcFiatId());
	}

	private getBfcFiatId(): string {
		let fiatId: string = "";
		if (!empty(this.transaction.getBfcFiatId())) {
			fiatId = this.transaction.getBfcFiatId();
		} else {
			fiatId = this.accountService.getDefaultFiat().getId();
		}
		return fiatId;
	}

	public getBestFee(): string {
		if (this.transaction instanceof TradableTransaction && this.transaction.getBfcFeeAmount().length > 0) {
			return this.transaction.getBfcFeeAmount();
		}

		if (this.transaction.hasTrade() && this.transaction.getTrade().getBfcUsed()) {
			return "" + this.transaction.getTrade().getBestFeeCollection().getRelatedBestTransaction().getFee();
		}

		if (this.transaction.getIsBfc() && this.storage_fee) {
			return this.transaction.getBestFeeCollection().getRelatedBestTransaction().getFee().toString();
		}

		if (this.paidFeesWithBest() && this.transaction instanceof TradableTransaction) {
			return this.transaction.getFee() + "";
		}

		return "";
	}

	public getBestTransactionPrice(usedPrice: boolean = true): string {
		const amount: number = usedPrice
			? +this.bfcEntity.getUsedBestPriceEur()
			: +this.bfcEntity.getCurrentBestPriceEur();
		if (this.transaction.hasTrade() && this.transaction.getTrade().getBfcUsed()) {
			return "" + toCurrencyByFiatId(amount, this.txFiat);
		}

		if (this.paidFeesWithBest() && this.getBfcTradeFiat()) {
			return "" + toCurrencyByFiatId(amount, this.getBfcTradeFiat());
		}

		return "";
	}

	public showHistoricPrice(): boolean {
		return !equal(this.bfcEntity.getUsedBestPriceEur(), this.bfcEntity.getCurrentBestPriceEur());
	}

	protected get bfcEntity(): BestFeeCollection {
		let bfcEntity: BestFeeCollection = new BestFeeCollection();

		if (this.transaction.hasTrade() && this.transaction.getTrade().getBfcUsed()) {
			bfcEntity = this.transaction.getTrade().getBestFeeCollection();
		} else if (this.paidFeesWithBest() && !!this.getBfcTradeFiat()) {
			bfcEntity = this.transaction.getBestFeeCollection();
		}
		return bfcEntity;
	}

	public getBestDiscountPercent(): string {
		const percentage: number =
			(1 -
				(1 - this.bfcEntity.getBestDeduction()) *
					Math.min(+this.bfcEntity.getCurrentBestPriceEur() / +this.bfcEntity.getUsedBestPriceEur(), 1)) *
			100;
		return round(percentage, 0) + "%";
	}

	public showWalletNameLink(): boolean {
		return !this.getToWallet() && this.transaction.getType() === "withdrawal" && this.transaction.isOutgoing();
	}

	private getTxFiat(): Fiat {
		if (this.transaction.hasTrade()) {
			return this.fiatService.getItemById(this.transaction.getTrade().getFiatId());
		}

		if (this.transaction) {
			return null;
		}
	}

	public get txFiat(): Fiat {
		return this._txFiat || this.getTxFiat();
	}

	public set txFiat(txFiat: Fiat) {
		this._txFiat = txFiat;
	}

	public getFromWallet(): Wallet {
		if (this.transaction.getType() === "buy") {
			return this.fiatWallet;
		}

		if (this.transaction.getType() === "sell" || this.isInternal) {
			return this.tradableWallet;
		}

		return null;
	}

	public getToWallet(): Wallet {
		if (this.transaction.getType() === "buy" || this.transaction.isRefund() || this.isInternal) {
			return this.tradableWallet;
		}

		if (this.transaction.getType() === "sell") {
			return this.fiatWallet;
		}

		return null;
	}

	public getFromWalletName(): string {
		if (
			(this.transaction.getType() === "transfer" &&
				!this.transaction.isOutgoing() &&
				this.getRecipient().length > 0) ||
			this.transaction.isRefund() ||
			(this.isInternal && !this.getFromWallet())
		) {
			return this.transaction.getFrom();
		}
		if (this.getFromWallet()) {
			return this.getFromWallet().getName();
		}
		if (this.transaction.hasTrade()) {
			return this.transaction.getTrade().isVoucher
				? this.translationService.translate("bitpandatogo_primary_field")
				: "";
		}
		return "";
	}

	public getToWalletName(): string {
		if (this.transaction.getType() === "transfer" || (this.isInternal && !this.getToWallet())) {
			return this.getRecipient();
		}
		if (this.getToWallet()) {
			return this.getToWallet().getName();
		}
		return "";
	}

	public openSwapDetails(): void {
		if (this.getRelatedSwap()) {
			this.modalService
				.create(TransactionWrapperModalComponent, {
					transaction: this.setSwapTradableTransaction(),
					canOpen: false,
					closeAllPopups: true
				})
				.present();
		} else {
			this.modalService.getCurrentModal().dismiss();
		}
	}

	public openBestDetails(): void {
		if (this.isBestMetalStorageFee()) {
			return;
		}

		if (!empty(this.transaction.getBfcFeeAmount()) || !empty(this.transaction.getBfcFiatId())) {
			const indexModal: Modal = this.modalService.stack.find((modal) => {
				const tempTx: TradableTransaction = modal.inputs.valueOf().transaction;
				return tempTx.isIndex() && tempTx.getWalletId() === this.transaction.getBfcRelatedWalletId();
			});
			if (indexModal) {
				// Finds an IndexTx on the stack and opens picks it out, then wipes the stack and starts fresh
				const indexTx: IndexTransaction = indexModal.inputs.valueOf().transaction;
				this.modalService.popAll();
				setTimeout(() => this.openTransactionModal(indexTx), 100);
			} else {
				this.modalService.getCurrentModal().dismiss();
			} // Dismiss for regular crypto transactions
			return; // Closes open modal on back-trace instead of re-opening an endless loop
		}

		if (this.transaction.getIsBfc || this.bfcEntity.getRelatedBestTransaction()) {
			let transaction: TradableTransaction = null;

			if (this.transaction.getIsBfc() && this.transaction instanceof TradableTransaction) {
				// The first part opens a related trade from a BEST Trading Fee
				if (this.bfcEntity.getRelatedIndexTransaction().isIndex()) {
					transaction = this.bfcEntity.getRelatedIndexTransaction();
				} else {
					transaction = convertTradeToTransaction(
						this.bfcEntity.getRelatedBestTrade(),
						new GenericTransaction(),
						this.getBfcTradeFiat()
					);
				}
				transaction.setBestFeeCollectionTrade(this.bfcEntity);
				transaction.setBfcFeeAmount(this.transaction.getFee() + "");
			} else {
				// This part opens BEST Trading Fee from a trade or storage fee
				transaction = this.bfcEntity.getRelatedBestTransaction();
				transaction.setBestFeeCollectionTransaction(this.bfcEntity);
				transaction.setBfcFiatId(this.accountService.getDefaultFiat().getId());
				const cryptoWalletId: string =
					this.transaction instanceof FiatTransaction
						? this.transaction.getTrade().getWalletId()
						: this.transaction.getWalletId(); // If fiatTx we need to look at the trade
				transaction.setBfcRelatedWalledId(cryptoWalletId);
			}
			this.openTransactionModal(transaction);
		}
	}

	public openDebitCardDetails(): void {
		if (!this.transaction.hasDebitCardRelatedTx()) {
			return;
		}
		const tx = this.transaction.getDebitCardRelatedTx();
		tx.setAmounts();
		this.modalService
			.create(TransactionWrapperModalComponent, {
				transaction: tx,
				canOpen: false,
				closeAllPopups: true
			})
			.present();
	}

	private openTransactionModal(transaction: TradableTransaction): void {
		this.modalService
			.create(TransactionWrapperModalComponent, {
				transaction: transaction,
				canOpen: false
			})
			.present();
	}

	public getRelatedSwap(): Trade {
		if (this.containsSwap()) {
			if (this.transaction.hasTrade()) {
				return this.transaction.getTrade().getRelatedSwapTrade();
			}
		}
		return null;
	}

	public setSwapTradableTransaction(): TradableTransaction {
		const txTemplate: TradableTransaction = this.getRelatedSwap().getIsIndex()
			? new IndexTransaction()
			: new GenericTransaction();
		return convertTradeToTransaction(
			this.getRelatedSwap(),
			txTemplate,
			this.fiatService.getItemById(this.getRelatedSwap().getFiatId())
		);
	}

	public getGiveawayReward(): string {
		if (this.transaction.hasTrade()) {
			return this.transaction.getTrade().getRewardCampaign();
		}
		return "";
	}

	public isFiatSend(): boolean {
		return this.getPayoutProvider() ? this.getPayoutProvider().sendEnabled(true) : false;
	}

	public isLeveragedTx(): boolean {
		return this.tradable instanceof LeveragedToken;
	}

	public getLeverageInfo(starting: boolean = true): string {
		if (this.isLeveragedTx()) {
			return starting
				? this.transaction.getTrade()["starting_borrow"]
				: this.transaction.getTrade()["closing_borrow"];
		}
		return "";
	}

	protected getPayoutProvider(): PayoutProvider {
		if (!(this.transaction instanceof FiatTransaction)) {
			return null;
		}

		const provider = this.masterdataService.payout_providers.find(
			(p) => p.getId() === (<FiatTransaction>this.transaction).getPayoutProviderId()
		);
		if (provider) {
			return provider;
		}
		return null;
	}

	public getDebitCardTxCountry(): string {
		if (!this.debitCardTxCountry) {
			return "";
		}
		return (this.transaction.getMerchantDetails().getCity() ? ", " : "") + this.debitCardTxCountry.getName();
	}
}
